import {
    ExcelToJsonParams,
    ExcelToJsonAIParams,
    GenerateCodesParams,
    GenerateCodesStoriesParams,
    GetUnlimitedTokenParams,
    CreateTrackTextTranslationParams,
    GetTrackLanguagesParams,
    GetWaitingTrackTranslationAIParams,
    ApplyUpdatesToTrackParams,
    DeleteTrackLanguageParams,
    GetWaitingTrackAIParams,
    ResetTrackParams,
    SaveTemporaryTrackParams,
} from "../types/requestInputs";
import {
    GenerateCodesResponse,
    GetStopsForTrackResponse,
    GetTemporaryTrackResponse,
    GetTrackLanguagesResponse,
    GetUnlimitedTokenResponse,
    GetWaitingTrackAIResponse,
    GetWaitingTrackTranslationAIResponse,
    SaveTemporaryTrackResponse,
    ShowMissingInfoByTrackIdResponse,
} from "../types/requestsOutputs";
import { MessageResponse } from "../types/types";
import { fetchAPI } from "./utils";

// Save a temporary track
export const saveTemporaryTrack = async ({
    token,
    trackData,
}: SaveTemporaryTrackParams): Promise<SaveTemporaryTrackResponse> => {
    return fetchAPI<SaveTemporaryTrackResponse>("/temp-track-creation", "POST", trackData, token);
};

// Get a temporary track by ID
export const getTemporaryTrack = async ({
    token,
    id,
}: {
    token: string;
    id: string;
}): Promise<GetTemporaryTrackResponse> => {
    return fetchAPI<GetTemporaryTrackResponse>("/get-temp-track", "POST", { id }, token);
};

// Reset a track
export const resetTrack = async ({ token, trackId }: ResetTrackParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/reset-track", "POST", { trackId }, token);
};

// Delete a track's language
export const deleteTrackLanguage = async ({ token, trackId, language }: DeleteTrackLanguageParams): Promise<String> => {
    return fetchAPI<String>("/delete-track-language", "POST", { trackId, language }, token);
};

// Get waiting AI track data
export const getWaitingTrackAI = async ({
    token,
    trackId,
}: GetWaitingTrackAIParams): Promise<GetWaitingTrackAIResponse> => {
    return fetchAPI<GetWaitingTrackAIResponse>("/get-track-ai-waiting", "POST", { trackId }, token);
};

// Apply updates to a track
export const applyUpdatesToTrack = async ({
    token,
    trackId,
    triggerModeFilter,
    excludeTriggerMode,
}: ApplyUpdatesToTrackParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>(
        "/apply-updates-track",
        "POST",
        { trackId, ...(triggerModeFilter && { triggerModeFilter }), ...(excludeTriggerMode && { excludeTriggerMode }) },
        token,
    );
};

// Convert Excel to JSON for waypoints
export const excelToJson = async ({ token, path, trackId }: ExcelToJsonParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/excel-waypoint-generation", "POST", { path, trackId }, token);
};

// Convert Excel to JSON using AI for waypoints
export const excelToJsonAI = async ({ token, path, trackId, trackData }: ExcelToJsonAIParams): Promise<String> => {
    return fetchAPI<String>("/excel-waypoint-generation-ai", "POST", { path, trackId, ...trackData }, token);
};

export const getUnlimitedToken = async ({
    token,
    trackId,
}: GetUnlimitedTokenParams): Promise<GetUnlimitedTokenResponse> => {
    return fetchAPI<GetUnlimitedTokenResponse>("/get-unlimited-token", "POST", { trackId }, token);
};

// Generate a set number of access tokens for a track
export const generateCodes = async ({
    token,
    trackId,
    numberTokens,
}: GenerateCodesParams): Promise<GenerateCodesResponse> => {
    return fetchAPI<GenerateCodesResponse>("/generate-tokens", "POST", { trackId, numberTokens }, token);
};

// Generate codes for stories within a track
export const generateCodesStories = async ({ token, trackId }: GenerateCodesStoriesParams): Promise<String> => {
    return fetchAPI<String>("/generate-codes-stories", "POST", { trackId }, token);
};

// Create AI-generated text translation for a track
export const createTrackTextTranslation = async ({
    token,
    trackId,
    language,
}: CreateTrackTextTranslationParams): Promise<String> => {
    return fetchAPI<String>("/create-track-text-translation", "POST", { trackId, language }, token);
};

// Get waiting AI-generated track translation
export const getWaitingTrackTranslationAI = async ({
    token,
    id,
}: GetWaitingTrackTranslationAIParams): Promise<GetWaitingTrackTranslationAIResponse> => {
    return fetchAPI<GetWaitingTrackTranslationAIResponse>(
        "/get-track-waiting-translation",
        "POST",
        { trackId: id },
        token,
    );
};

// Get available languages for a track
export const getTrackLanguages = async ({ token, id }: GetTrackLanguagesParams): Promise<GetTrackLanguagesResponse> => {
    return fetchAPI<GetTrackLanguagesResponse>("/get-track-languages", "POST", { trackId: id }, token);
};

// Show missing info for a track
export const showMissingInfoByTrackId = async ({
    token,
    trackId,
}: {
    token: string;
    trackId: string;
}): Promise<ShowMissingInfoByTrackIdResponse> => {
    return fetchAPI<ShowMissingInfoByTrackIdResponse>("/check-missing-info-language", "POST", { trackId }, token);
};

// Get all stops for a track
export const getStopsForTrack = async ({
    token,
    trackId,
}: {
    token: string;
    trackId: string;
}): Promise<GetStopsForTrackResponse> => {
    return fetchAPI<GetStopsForTrackResponse>("/get-track-stops", "POST", { trackId }, token);
};
